// ─────────────────────────────────────────────────────────────────────────
// buildComparisonPdfModel(comparison, options)
//
// Transforms the SAME comparison object buildReleaseComparison() already
// produced (and ReleaseComparisonView.jsx already rendered on screen) into
// a printable model. Zero financial calculation — every baseValue/
// compareValue/variance/status here is read as-is from `comparison`, never
// recomputed. Zero note-fetching — `options.notes` is the exact notes map
// ReleaseComparisonView.jsx already loaded/holds in its own state; this
// function only re-attaches each note string onto its matching row for
// display, exactly as it exists on screen at export time (per explicit
// requirement: "export exactly what the user is viewing").
//
// SECTIONS ARE WHATEVER THE SCREEN ALREADY COMPUTED: comparison.directDiffs
// and comparison.indirectDiffs already reflect the active mode ('full' vs
// 'changed') and scope (project vs a single Cost Code subtree) — this
// function does not re-filter, re-scope, or re-mode anything. If the caller
// generated the comparison in 'changed' mode, the PDF shows exactly the
// changed-only tree; there is no hidden "export everything" fallback.

function flattenNote(notes, nodeType, code) {
  const key = `${nodeType}:${code}`
  return notes?.[key]?.note || ''
}

function adaptRow(node, nodeType, notes) {
  return {
    code: node.code,
    description: node.description,
    status: node.status,
    baseValue: node.baseValue,
    compareValue: node.compareValue,
    variance: node.variance,
    note: flattenNote(notes, nodeType, node.code),
  }
}

// Flattens one Cost Code subtree (Cost Code → Resource → Sub Code, matching
// the exact grouping ReleaseComparisonView.jsx already renders) into a flat
// print-ready row list with `indent` for display, preserving parent-chain
// context exactly as the screen's expand/collapse already does — the PDF
// prints every row the screen would show fully expanded.
function flattenCostCodeSubtree(cc, notes) {
  const rows = [{ ...adaptRow(cc, 'cost_code', notes), indent: 0 }]

  const orphanChildren = (cc.children || []).filter(c => !c.parent_id)
  const childrenByParent = {}
  for (const c of (cc.children || [])) {
    if (c.parent_id) (childrenByParent[c.parent_id] = childrenByParent[c.parent_id] || []).push(c)
  }

  for (const res of (cc.resourceRows || [])) {
    rows.push({ ...adaptRow(res, 'resource', notes), indent: 1 })
    for (const sub of (childrenByParent[res.code] || [])) {
      rows.push({ ...adaptRow(sub, 'sub_code', notes), indent: 2 })
    }
  }
  for (const sub of orphanChildren) {
    rows.push({ ...adaptRow(sub, 'sub_code', notes), indent: 1 })
  }

  return rows
}

function buildSection(title, diffs, notes) {
  const rows = []
  for (const cc of diffs) rows.push(...flattenCostCodeSubtree(cc, notes))
  return { title, rows }
}

export function buildComparisonPdfModel(comparison, options = {}) {
  const {
    projectName = '',
    orgName = '',
    logoUrl = null,
    baseRelease = null,
    compareRelease = null,
    mode = 'full',
    scope = 'project',
    notes = {},
    generatedBy = '',
  } = options

  const releaseLabel = (r) => r
    ? {
        number: r.release_number,
        period: r.forecast_date ? new Date(r.forecast_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—',
        directRevisionLabel: r.direct_snapshot_id ? `Rev.${r.direct_revision_number}` : 'None',
        indirectRevisionLabel: r.indirect_snapshot_id ? `Rev.${r.indirect_revision_number}` : 'None',
      }
    : null

  const header = {
    projectName,
    orgName,
    logoUrl,
    reportTitle: 'Forecast Release Comparison Report',
    generatedDate: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' }),
    generatedBy,
    base: releaseLabel(baseRelease),
    // revisionMeta — read directly from comparison.revisionMeta (the same
    // object ComparingBar already renders on screen), not recomputed here.
    // Lets the PDF show the identical "same revision — no change expected"
    // flag the screen shows, for the exact Direct/Indirect Revision pair
    // being compared.
    revisionMeta: comparison.revisionMeta || null,
    compare: releaseLabel(compareRelease),
    modeLabel: mode === 'changed' ? 'Changed Items Only' : 'Full Tree',
    scopeLabel: scope === 'project' ? 'Entire Project' : `Selected Cost Code — ${scope.costCodeCode || ''}`,
  }

  // Summary — read directly from comparison.summary, no recalculation.
  const summary = {
    totalVariance: comparison.summary.totalVariance,
    totalVariancePct: comparison.summary.totalVariancePct,
    changedCount: comparison.summary.changedCount,
    newCount: comparison.summary.newCount,
    removedCount: comparison.summary.removedCount,
    topVariances: comparison.summary.topVariances.map(n => ({
      code: n.code, description: n.description, variance: n.variance, level: n.level,
    })),
  }

  const sections = []
  if (comparison.directDiffs.length > 0) sections.push(buildSection('Direct Works', comparison.directDiffs, notes))
  if (comparison.indirectDiffs.length > 0) sections.push(buildSection('Indirect Works', comparison.indirectDiffs, notes))

  return { header, summary, sections }
}
